import axios from 'axios';
import type { ExpenseLimit } from '../types/expense';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

// Export for components that need raw fetch (e.g., streaming)
export { API_BASE_URL };

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and language
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add Accept-Language header based on stored language preference
  const language = localStorage.getItem('i18nextLng') || 'en';
  config.headers['Accept-Language'] = language;

  return config;
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } =
            response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  },
);

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  role: string;
  employment_type:
    | 'employee'
    | 'contractor'
    | 'intern'
    | 'part_time'
    | 'consultant';
  active: boolean;
  salary?: number | null;
  hours_per_week?: number | null;
  province_code?: string | null;
  specialty_ids?: number[];
  specialties?: Specialty[];
  organization: {
    id: number;
    name: string;
    has_subscription?: boolean;
    trialing?: boolean;
    trial_days_remaining?: number;
    subscription_status?: string;
  } | null;
}

export interface Project {
  id: number;
  name: string;
  project_code?: string;
  description?: string;
  sred_eligible: boolean;
  cdae_eligible: boolean;
  status: string;
  start_date?: string;
  end_date?: string;
  client_id?: number;
  is_internal: boolean;
  client?: Client;
  specialty_ids?: number[];
  specialties?: Specialty[];
}

export interface SuggestedUser {
  id: number;
  full_name: string;
  employment_type:
    | 'employee'
    | 'contractor'
    | 'intern'
    | 'part_time'
    | 'consultant';
  match_count: number;
  matched_specialties: { id: number; name: string }[];
}

export interface SredActivityCategory {
  id: number;
  name: string;
  code: string;
  description?: string;
  category_type: string;
  is_eligible: boolean;
}

export interface Tag {
  id: number;
  name: string;
  color: string;
  task_count?: number;
  time_entry_count?: number;
}

export interface Specialty {
  id: number;
  name: string;
  user_count?: number;
}

export interface Task {
  id: number;
  title: string;
  description?: string;
  project_id: number;
  project?: { id: number; name: string };

  assigned_to_user_id?: number;
  status: string;
  priority: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  is_billable?: boolean;
  billable_rate?: number;
  tags?: Tag[];
  tag_ids?: number[];
}

export interface Client {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  default_rate?: number;
}

export interface ComplianceValidation {
  validation_type: string;
  validation_type_name: string;
  is_valid: boolean;
  validation_message: string;
  validated_at: string;
  validated_by_system: boolean;
}

export interface Pricelist {
  id: number;
  name: string;
  description?: string;
  billable_rate: number;
  cost_rate: number;
  currency: 'CAD' | 'USD';
  effective_date: string;
  scope_description: string;
  formatted_billable_rate: string;
  formatted_cost_rate: string;
  created_at?: string;
  updated_at?: string;
  client?: {
    id: number;
    name: string;
    company?: string;
  } | null;
  project?: {
    id: number;
    name: string;
    is_internal: boolean;
  } | null;
  user?: {
    id: number;
    full_name: string;
    email: string;
    role: string;
  } | null;
}

export interface Team {
  id: number;
  name: string;
  description?: string;
  active: boolean;
  members_count: number;
  managers?: User[];
  developers?: User[];
  memberships?: TeamMembership[];
  created_at: string;
  updated_at: string;
}

export interface TeamMembership {
  id: number;
  user: User;
  role: 'member' | 'manager';
  active: boolean;
  joined_at: string;
}

export interface TimeEntry {
  id?: number;
  project_id: number;
  task_id?: number;
  sred_activity_category_id: number;
  entry_date: string;
  start_time?: string;
  end_time?: string;
  hours_worked: number;
  description: string;
  technical_details?: string;
  challenges_encountered?: string;
  methodologies_used?: string;
  results_achieved?: string;
  uncertainty_addressed?: string;
  advancement_sought?: string;
  technical_challenge?: string;
  advancement?: string;
  is_sred_eligible?: boolean;
  is_cdae_eligible?: boolean;
  is_billable?: boolean;
  billable_rate?: number;
  invoiced?: boolean;
  invoiced_at?: string;
  invoiced_by_invoice_id?: string;
  client_id?: number;
  approval_status?: string;
  rejection_reason?: string;
  compliance_score?: number;
  created_at?: string;
  updated_at?: string;
  // AI Analysis fields
  ai_analysis_data?: any;
  ai_confidence_score?: number;
  ai_analyzed_at?: string;
  ai_sred_reasoning?: string;
  ai_cdae_reasoning?: string;
  ai_matched_keywords?: string[];
  ai_suggestions?: any[];
  user?: User;
  project?: Project;
  task?: Task;
  sred_activity_category?: SredActivityCategory;
  approved_by?: User;
  compliance_validations?: ComplianceValidation[];
  tags?: Tag[];
  tag_ids?: number[];
}

export interface TimeClockEntry {
  id: number;
  started_at: string;
  ended_at: string | null;
  status: 'started' | 'stopped';
  description: string | null;
  duration_seconds: number;
  duration_hours: number;
  duration_formatted: string;
  is_active: boolean;
  converted_to_time_entry_id: number | null;
  tag_ids?: number[];
  project: Project;
  task?: Task;
  user: User;
}

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (userData: {
    user: {
      first_name: string;
      last_name: string;
      email: string;
      password: string;
      role: string;
    };
    organization?: {
      name: string;
      address?: string;
      contact_email?: string;
    };
    plan_id?: number;
    quantity?: number;
  }) => api.post('/auth/register', userData),

  logout: () => api.delete('/auth/logout'),

  getProfile: () => api.get('/auth/profile'),

  // Self-service profile update (province + weekly hours only; salary is admin-only)
  updateProfile: (data: {
    province_code?: string | null;
    hours_per_week?: number | null;
  }) => api.patch('/auth/profile', { user: data }),
};

// Time Entries API
export const timeEntriesAPI = {
  getAll: (params?: Record<string, any>) =>
    api.get('/time_entries', { params }),

  getById: (id: number) => api.get(`/time_entries/${id}`),

  create: (timeEntry: Omit<TimeEntry, 'id'>) =>
    api.post('/time_entries', { time_entry: timeEntry }),

  update: (id: number, timeEntry: Partial<TimeEntry>) =>
    api.put(`/time_entries/${id}`, { time_entry: timeEntry }),

  delete: (id: number) => api.delete(`/time_entries/${id}`),

  approve: (id: number) => api.patch(`/time_entries/${id}/approve`),

  reject: (id: number, reason: string) =>
    api.patch(`/time_entries/${id}/reject`, { reason }),

  bulkApprove: (timeEntryIds: number[]) =>
    api.patch('/time_entries/bulk_approve', { time_entry_ids: timeEntryIds }),

  bulkUpdate: (timeEntryIds: number[], updates: Record<string, any>) =>
    api.patch('/time_entries/bulk_update', {
      time_entry_ids: timeEntryIds,
      updates: updates,
    }),

  bulkReject: (timeEntryIds: number[], reason?: string) =>
    api.patch('/time_entries/bulk_reject', {
      time_entry_ids: timeEntryIds,
      rejection_reason: reason,
    }),

  bulkDelete: (timeEntryIds: number[]) =>
    api.delete('/time_entries/bulk_delete', {
      data: { time_entry_ids: timeEntryIds },
    }),

  bulkMarkInvoiced: (
    timeEntryIds: number[],
    invoiced: boolean,
    invoiceId?: string,
  ) =>
    api.patch('/time_entries/invoiced', {
      time_entry_ids: timeEntryIds,
      invoiced,
      invoiced_by_invoice_id: invoiceId,
    }),

  uninvoice: (timeEntryIds: number[]) =>
    api.patch('/time_entries/uninvoice', {
      time_entry_ids: timeEntryIds,
    }),

  getReports: (params?: {
    client_id?: number;
    project_id?: number;
    user_id?: number;
    start_date?: string;
    end_date?: string;
    billable?: 'yes' | 'no' | 'all';
    invoiced?: 'yes' | 'no' | 'all';
    report_type?: 'summary' | 'detailed';
  }) => api.get('/time_entries/reports', { params }),

  getSredReport: (params?: {
    client_id?: number;
    project_id?: number;
    start_date?: string;
    end_date?: string;
  }) => api.get('/time_entries/sred_report', { params }),

  getSredReportStatus: (reportId: string) =>
    api.get('/time_entries/sred_report', { params: { report_id: reportId } }),

  // Pre-submission compliance coach: pending entries below the score threshold.
  getComplianceSummary: (params?: { threshold?: number }) =>
    api.get('/time_entries/compliance_summary', { params }),

  // Queue background AI analysis for pending entries that were never analyzed.
  analyzePending: () => api.post('/time_entries/analyze_pending'),

  // Draft a description from the user's GitHub/Jira activity on a date.
  getActivitySuggestions: (params: { project_id: number; date?: string }) =>
    api.get('/time_entries/activity_suggestions', { params }),
};

// Time Clock Entries API (Timer feature)
export const timeClockAPI = {
  start: (
    projectId?: number,
    taskId?: number,
    tagIds?: number[],
    description?: string,
  ) =>
    api.post<{ time_clock_entry: TimeClockEntry }>(
      '/time_clock_entries/start',
      {
        project_id: projectId,
        task_id: taskId,
        tag_ids: tagIds,
        description,
      },
    ),

  stop: (id: number) =>
    api.patch<{ time_clock_entry: TimeClockEntry }>(
      `/time_clock_entries/${id}/stop`,
    ),

  getCurrentTimer: () =>
    api.get<{ time_clock_entry: TimeClockEntry | null }>(
      '/time_clock_entries/current_timer',
    ),

  convertToTimeEntry: (
    id: number,
    data: { description: string; sred_activity_category_id?: number },
  ) =>
    api.post<{ time_entry: TimeEntry; time_clock_entry: TimeClockEntry }>(
      `/time_clock_entries/${id}/convert_to_time_entry`,
      data,
    ),

  update: (
    id: number,
    data: {
      description?: string;
      project_id?: number | null;
      task_id?: number | null;
      tag_ids?: number[];
    },
  ) =>
    api.patch<{ time_clock_entry: TimeClockEntry }>(
      `/time_clock_entries/${id}`,
      data,
    ),

  delete: (id: number) => api.delete(`/time_clock_entries/${id}`),
};

// Compliance API
export const complianceAPI = {
  analyzeDescription: (
    description: string,
    projectContext?: { is_sred_eligible?: boolean },
  ) =>
    api.post('/compliance/analyze_description', {
      description,
      project_context: projectContext,
    }),
  suggestImprovements: (description: string, analysisData?: any) =>
    api.post('/compliance/suggest_improvements', {
      description,
      analysis_data: analysisData,
    }),
  // For streaming chat, use API_BASE_URL directly with fetch
};

// Projects API
export const projectsAPI = {
  getAll: (params?: {
    sred_eligible?: boolean;
    cdae_eligible?: boolean;
    client_id?: number;
    status?: string;
  }) => api.get('/projects', { params }),
  getById: (id: number) => api.get(`/projects/${id}`),
  suggestedUsers: (id: number) =>
    api.get<{ suggested_users: SuggestedUser[] }>(`/projects/${id}/suggested_users`),
  create: (project: Omit<Project, 'id'>) => api.post('/projects', { project }),
  update: (id: number, project: Partial<Project>) =>
    api.put(`/projects/${id}`, { project }),
  delete: (id: number) => api.delete(`/projects/${id}`),
  list: (params?: { status?: string; managerId?: number }) =>
    api.get('/projects', { params }) as Promise<{ data: { projects: Project[] } }>,
};

// Tasks API
export const tasksAPI = {
  getAll: (params?: Record<string, any>) => api.get('/tasks', { params }),
  getById: (id: number) => api.get(`/tasks/${id}`),
  create: (task: Omit<Task, 'id'>) => api.post('/tasks', { task }),
  update: (id: number, task: Partial<Task>) =>
    api.put(`/tasks/${id}`, { task }),
  delete: (id: number) => api.delete(`/tasks/${id}`),
  bulkMove: (taskIds: number[], boardColumnId?: number, sprintId?: number) =>
    api.patch('/tasks/bulk_move', {
      task_ids: taskIds,
      board_column_id: boardColumnId,
      sprint_id: sprintId,
    }),
  bulkDelete: (taskIds: number[]) =>
    api.delete('/tasks/bulk_delete', { data: { task_ids: taskIds } }),
  list: (params?: {
    status?: string;
    projectId?: number;
    assigneeId?: number;
  }) => api.get('/tasks', { params }) as Promise<{ data: { tasks: Task[] } }>,
};

// Tags API
export const tagsAPI = {
  getAll: () => api.get('/tags'),
  create: (tag: { name: string; color: string }) => api.post('/tags', { tag }),
  update: (id: number, tag: { name: string; color: string }) =>
    api.put(`/tags/${id}`, { tag }),
  delete: (id: number) => api.delete(`/tags/${id}`),
};

// Specialties API (org-scoped managed list; admin-only writes)
export const specialtiesAPI = {
  getAll: () => api.get<Specialty[]>('/specialties'),
  create: (name: string) => api.post<Specialty>('/specialties', { specialty: { name } }),
  update: (id: number, name: string) =>
    api.put<Specialty>(`/specialties/${id}`, { specialty: { name } }),
  delete: (id: number) => api.delete(`/specialties/${id}`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id: number) => api.get(`/users/${id}`),
  create: (userData: any) => api.post('/users', { user: userData }),
  update: (id: number, user: Partial<User>) =>
    api.put(`/users/${id}`, { user }),
  invite: (userData: any) => api.post('/users/invite', { user: userData }),
  resendInvitation: (id: number) => api.post(`/users/${id}/resend_invitation`),
  delete: (id: number) => api.delete(`/users/${id}`),
};

// Invitations API (public endpoints, no auth required)
export const invitationsAPI = {
  validate: (token: string) => api.get(`/invitations/${token}`),
  accept: (token: string, password: string) =>
    api.post(`/invitations/${token}/accept`, { password }),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getById: (id: number) => api.get(`/clients/${id}`),
  create: (client: Omit<Client, 'id'>) => api.post('/clients', { client }),
  update: (id: number, client: Partial<Client>) =>
    api.put(`/clients/${id}`, { client }),
  delete: (id: number) => api.delete(`/clients/${id}`),
};

// SR&ED Activity Categories API
export const sredCategoriesAPI = {
  getAll: () => api.get('/sred_activity_categories'),
  getById: (id: number) => api.get(`/sred_activity_categories/${id}`),
};

// SR&ED Reports API (saved reports management)
export interface SavedSredReport {
  id: number;
  report_id: string;
  name: string;
  status: string;
  progress: number;
  filters: {
    client_id?: number;
    project_id?: number;
    start_date?: string;
    end_date?: string;
  };
  report_data?: any;
  created_at: string;
  updated_at: string;
}

export const sredReportsAPI = {
  getAll: () => api.get<{ sred_reports: SavedSredReport[] }>('/sred_reports'),
  getById: (id: number) =>
    api.get<{ sred_report: SavedSredReport }>(`/sred_reports/${id}`),
  delete: (id: number) => api.delete(`/sred_reports/${id}`),
  refresh: (id: number) =>
    api.post<{
      report_id: string;
      sred_report_id: number;
      status: string;
      message: string;
    }>(`/sred_reports/${id}/refresh`),
};

// Time Off Requests API
export const timeOffRequestsAPI = {
  getAll: () => api.get('/time_off_requests'),
  getById: (id: number) => api.get(`/time_off_requests/${id}`),
  create: (request: any) =>
    api.post('/time_off_requests', { time_off_request: request }),
  update: (id: number, request: any) =>
    api.put(`/time_off_requests/${id}`, { time_off_request: request }),
  delete: (id: number) => api.delete(`/time_off_requests/${id}`),
  approve: (id: number) => api.patch(`/time_off_requests/${id}/approve`),
  reject: (id: number, reason: string) =>
    api.patch(`/time_off_requests/${id}/reject`, {
      time_off_request: { rejection_reason: reason },
    }),
  calendar: (year: number, month: number, day?: number) =>
    api.get('/time_off_requests/calendar', { params: { year, month, day } }),
};

export const timeOffBalancesAPI = {
  getMyBalance: async () => {
    try {
      const response = await api.get('/time_off_balances/mine');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 422) {
        return null;
      }
      throw error;
    }
  },
  teamSummary: (year: number) =>
    api.get('/time_off_balances/team_summary', { params: { year } }),
};

export const expensesAPI = {
  getAll: (filters?: any) => api.get('/expenses', { params: filters }),
  getMine: (params?: Record<string, unknown>) =>
    api.get('/expenses/mine', { params }),
  getById: (id: number) => api.get(`/expenses/${id}`),
  create: (expense: any) => api.post('/expenses', { expense }),
  update: (id: number, expense: any) => api.put(`/expenses/${id}`, { expense }),
  delete: (id: number) => api.delete(`/expenses/${id}`),
  submit: (id: number) => api.patch(`/expenses/${id}/submit`),
  approve: (id: number) => api.patch(`/expenses/${id}/approve`),
  reject: (id: number, reason: string) =>
    api.patch(`/expenses/${id}/reject`, {
      expense: { rejection_reason: reason },
    }),
  getPending: (filters?: any) =>
    api.get('/expenses/pending', { params: filters }),
  getReports: (filters?: any) =>
    api.get('/expenses/reports', { params: filters }),
  exportCSV: (filters?: any) =>
    api.get('/expenses/export.csv', { params: filters, responseType: 'blob' }),
};

export const expenseAttachmentsAPI = {
  upload: (expenseId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/expenses/${expenseId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (expenseId: number, attachmentId: number) =>
    api.delete(`/expenses/${expenseId}/attachments/${attachmentId}`),
  downloadUrl: (expenseId: number, attachmentId: number) =>
    `${api.defaults.baseURL}/expenses/${expenseId}/attachments/${attachmentId}/download`,
};

// Pricelists API
export const pricelistsAPI = {
  getAll: (params?: Record<string, any>) => api.get('/pricelists', { params }),
  getById: (id: number) => api.get(`/pricelists/${id}`),
  create: (
    pricelist: Omit<
      Pricelist,
      | 'id'
      | 'scope_description'
      | 'formatted_billable_rate'
      | 'formatted_cost_rate'
      | 'created_at'
      | 'updated_at'
    >,
  ) => api.post('/pricelists', { pricelist }),
  update: (id: number, pricelist: Partial<Pricelist>) =>
    api.put(`/pricelists/${id}`, { pricelist }),
  delete: (id: number) => api.delete(`/pricelists/${id}`),
  findRateFor: (params: {
    client_id?: number;
    project_id?: number;
    user_id?: number;
  }) => api.get('/pricelists/find_rate', { params }),
};

// Pricelist User Assignments API
export const pricelistUserAssignmentsAPI = {
  getAll: (pricelistId: number) => api.get(`/pricelists/${pricelistId}/users`),
  getById: (pricelistId: number, id: number) =>
    api.get(`/pricelists/${pricelistId}/users/${id}`),
  create: (
    pricelistId: number,
    assignment: { user_id: number; billable_rate: number; cost_rate: number },
  ) => api.post(`/pricelists/${pricelistId}/users`, { assignment }),
  update: (
    pricelistId: number,
    id: number,
    assignment: { billable_rate?: number; cost_rate?: number },
  ) => api.put(`/pricelists/${pricelistId}/users/${id}`, { assignment }),
  delete: (pricelistId: number, id: number) =>
    api.delete(`/pricelists/${pricelistId}/users/${id}`),
};

// Teams API
export const teamsAPI = {
  getAll: () => api.get('/teams'),
  getById: (id: number) => api.get(`/teams/${id}`),
  create: (
    team: Omit<
      Team,
      | 'id'
      | 'members_count'
      | 'managers'
      | 'developers'
      | 'memberships'
      | 'created_at'
      | 'updated_at'
    >,
  ) => api.post('/teams', { team }),
  update: (id: number, team: Partial<Team>) =>
    api.put(`/teams/${id}`, { team }),
  delete: (id: number) => api.delete(`/teams/${id}`),

  // Team membership management
  addMember: (
    teamId: number,
    userId: number,
    role: 'member' | 'manager' = 'member',
  ) => api.post(`/teams/${teamId}/add_member`, { user_id: userId, role }),
  removeMember: (teamId: number, membershipId: number) =>
    api.delete(`/teams/${teamId}/members/${membershipId}`),
  updateMemberRole: (
    teamId: number,
    membershipId: number,
    role: 'member' | 'manager',
  ) => api.patch(`/teams/${teamId}/members/${membershipId}`, { role }),
};

// AI Assistant Types
export interface AiMessage {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
}

export interface AiConversation {
  id: number;
  title?: string;
  context_type?: 'general' | 'time_entry_help' | 'time_entry_analysis';
  project_id?: number;
  project?: {
    id: number;
    name: string;
    client_name?: string;
  } | null;
  has_integration_data?: boolean;
  time_entry_id?: number;
  last_message_at?: string;
  created_at: string;
  message_count: number;
  messages?: AiMessage[];
}

export interface AiKnowledgeDocument {
  id: number;
  title: string;
  document_type: 'sred' | 'cdae' | 'general';
  language: 'en' | 'fr';
  active: boolean;
  content?: string;
  original_filename?: string;
  file_size?: number;
  uploaded_by?: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

export interface AiSystemPrompt {
  id: number;
  name: string;
  content: string;
  active: boolean;
  version: number;
  updated_by?: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

export interface AiQaExample {
  id: number;
  category: 'sred' | 'cdae' | 'general';
  language: 'en' | 'fr';
  question: string;
  answer: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Voice action result embedded in AI chat done event
export interface VoiceActionResult {
  action:
    | 'start_timer'
    | 'stop_timer'
    | 'log_time'
    | 'create_task'
    | 'create_project'
    | 'create_client'
    | 'navigate'
    | 'show_analytics'
    | 'unknown';
  success?: boolean;
  project_id?: number;
  project_name?: string;
  name?: string;
  budget?: number | string;
  hours?: number;
  description?: string;
  task_id?: number;
  task_title?: string;
  destination?: string;
  error?: string;
  message?: string;
}

// AI Assistant API
// Mutating action queued by the operational AI, awaiting user confirmation.
export interface PendingAiAction {
  tool: string;
  arguments: Record<string, unknown>;
}

export const aiAssistantAPI = {
  // Chat with streaming support
  chat: (params: {
    message: string;
    conversation_id?: number;
    context_type?: string;
    stream?: boolean;
  }) => api.post('/ai/chat', params),

  // Execute (approve: true) or cancel (approve: false) the pending action
  confirmAction: (conversationId: number, approve: boolean) =>
    api.post<{
      conversation_id: number;
      response: string;
      success: boolean;
      client_actions: VoiceActionResult[];
    }>('/ai/confirm_action', { conversation_id: conversationId, approve }),

  // Stream chat (returns EventSource URL params)
  streamChat: async (
    params: {
      message: string;
      conversation_id?: number;
      context_type?: string;
      project_id?: number;
      voice_mode?: boolean;
    },
    onChunk: (chunk: string) => void,
    onDone: (
      conversationId: number,
      actionResult?: VoiceActionResult,
      clientActions?: VoiceActionResult[],
      pendingAction?: PendingAiAction,
    ) => void,
    onError: (error: string) => void,
  ) => {
    const token = localStorage.getItem('access_token');
    const language = localStorage.getItem('i18nextLng') || 'en';

    if (!token) {
      onError('Authentication token not found. Please log in again.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept-Language': language,
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ ...params, stream: true }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.done) {
                onDone(
                  data.conversation_id,
                  data.action_result,
                  data.client_actions,
                  data.pending_action,
                );
              } else if (data.error) {
                onError(data.error);
              } else {
                onChunk(data);
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (error: any) {
      onError(error.message || 'Stream error');
    }
  },

  analyzeTimeEntry: (params: {
    time_entry_id: number;
    question?: string;
    conversation_id?: number;
    stream?: boolean;
  }) => api.post('/ai/analyze_time_entry', params),
};

// AI Conversations API
export const aiConversationsAPI = {
  getAll: (params?: { page?: number; per_page?: number }) =>
    api.get('/ai_conversations', { params }),

  getById: (id: number) => api.get(`/ai_conversations/${id}`),

  create: (params: {
    context_type?: string;
    time_entry_id?: number;
    project_id?: number;
  }) => api.post('/ai_conversations', params),

  update: (id: number, params: { project_id?: number | null }) =>
    api.put(`/ai_conversations/${id}`, params),

  delete: (id: number) => api.delete(`/ai_conversations/${id}`),
};

// AI Knowledge Documents API (Admin only)
export const aiKnowledgeDocumentsAPI = {
  getAll: (params?: {
    page?: number;
    per_page?: number;
    document_type?: string;
    language?: string;
    active?: string;
  }) => api.get('/ai_knowledge_documents', { params }),

  getById: (id: number) => api.get(`/ai_knowledge_documents/${id}`),

  create: (document: FormData) =>
    api.post('/ai_knowledge_documents', document, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  update: (id: number, document: FormData | Partial<AiKnowledgeDocument>) => {
    if (document instanceof FormData) {
      return api.put(`/ai_knowledge_documents/${id}`, document, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.put(`/ai_knowledge_documents/${id}`, { document });
  },

  delete: (id: number) => api.delete(`/ai_knowledge_documents/${id}`),
};

// AI System Prompts API (Admin only)
export const aiSystemPromptsAPI = {
  getAll: () => api.get('/ai_system_prompts'),

  getById: (id: number) => api.get(`/ai_system_prompts/${id}`),

  create: (prompt: { name: string; content: string }) =>
    api.post('/ai_system_prompts', { prompt }),

  update: (id: number, prompt: Partial<AiSystemPrompt>) =>
    api.put(`/ai_system_prompts/${id}`, { prompt }),

  delete: (id: number) => api.delete(`/ai_system_prompts/${id}`),

  activate: (id: number) => api.post(`/ai_system_prompts/${id}/activate`),
};

// AI QA Examples API (Admin only)
export const aiQaExamplesAPI = {
  getAll: (params?: {
    page?: number;
    per_page?: number;
    category?: string;
    language?: string;
    active?: string;
  }) => api.get('/ai_qa_examples', { params }),

  getById: (id: number) => api.get(`/ai_qa_examples/${id}`),

  create: (example: Omit<AiQaExample, 'id' | 'created_at' | 'updated_at'>) =>
    api.post('/ai_qa_examples', { example }),

  update: (id: number, example: Partial<AiQaExample>) =>
    api.put(`/ai_qa_examples/${id}`, { example }),

  delete: (id: number) => api.delete(`/ai_qa_examples/${id}`),

  bulkCreate: (
    examples: Array<Omit<AiQaExample, 'id' | 'created_at' | 'updated_at'>>,
  ) => api.post('/ai_qa_examples/bulk_create', { examples }),
};

// OAuth Connections Types
export interface OauthConnection {
  id: number;
  provider: 'google_drive' | 'onedrive' | 'github';
  account_email?: string;
  account_name?: string;
  active: boolean;
  created_at: string;
  token_expires_at?: string;
}

// Project Integration Types
export interface GoogleDriveIntegration {
  id: number;
  folder_id: string;
  folder_name?: string;
  folder_path?: string;
  drive_id?: string | null;
  drive_name?: string;
  is_shared_drive?: boolean;
  sync_enabled: boolean;
  sync_status?: string;
  last_sync_at?: string;
  last_sync_error?: string;
  synced_documents_count: number;
}

export interface GitHubIntegration {
  id: number;
  repo_full_name: string;
  repo_name?: string;
  default_branch: string;
  sync_commits: boolean;
  sync_prs: boolean;
  sync_issues: boolean;
  sync_code: boolean;
  sync_enabled: boolean;
  sync_status?: string;
  last_sync_at?: string;
  last_sync_error?: string;
  synced_items_count: number;
}

export interface OneDriveIntegration {
  id: number;
  folder_id: string;
  folder_name?: string;
  folder_path?: string;
  drive_id?: string;
  sync_enabled: boolean;
  sync_status?: string;
  last_sync_at?: string;
  last_sync_error?: string;
  synced_documents_count: number;
}

export interface JiraIntegration {
  id: number;
  cloud_id: string;
  site_name?: string;
  site_url?: string;
  jira_project_id?: string;
  jira_project_key?: string;
  jira_project_name?: string;
  sync_issues: boolean;
  sync_sprints: boolean;
  sync_epics: boolean;
  sync_enabled: boolean;
  sync_status?: string;
  last_sync_at?: string;
  last_sync_error?: string;
}

export interface IntegrationSyncLog {
  id: number;
  integration_type: string;
  status: string;
  items_synced: number;
  items_failed: number;
  error_message?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
}

export interface ProjectIntegrations {
  google_drive: GoogleDriveIntegration | null;
  onedrive: OneDriveIntegration | null;
  github: GitHubIntegration[];
  jira: JiraIntegration[];
}

export interface GitHubRepo {
  full_name: string;
  name: string;
  description?: string;
  default_branch: string;
  private: boolean;
  html_url: string;
  owner?: string;
  owner_type?: string;
}

export interface GoogleDriveFolder {
  id: string;
  name: string;
  has_children: boolean;
  drive_id?: string;
}

export interface GoogleDriveSharedDrive {
  id: string;
  name: string;
  type: 'shared_drive';
}

export interface OneDriveDrive {
  id: string;
  name: string;
  type: string;
  owner?: string;
}

export interface OneDriveFolder {
  id: string;
  name: string;
  has_children: boolean;
  drive_id?: string;
}

export interface JiraSite {
  id: string;
  name: string;
  url: string;
  scopes?: string[];
  avatar_url?: string;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
  project_type?: string;
  avatar_url?: string;
}

// OAuth Connections API
export const oauthConnectionsAPI = {
  getAll: () =>
    api.get<{ oauth_connections: OauthConnection[] }>('/oauth_connections'),

  authorize: (provider: string) =>
    api.get<{ authorization_url: string }>(
      `/oauth_connections/${provider}/authorize`,
    ),

  disconnect: (id: number) => api.delete(`/oauth_connections/${id}`),

  getGitHubRepos: () =>
    api.get<{ repos: GitHubRepo[] }>('/oauth_connections/github/repos'),

  getGoogleDriveFolders: (parentId?: string, driveId?: string) =>
    api.get<{
      folders: GoogleDriveFolder[];
      parent_id: string;
      drive_id?: string;
    }>('/oauth_connections/google_drive/folders', {
      params: { parent_id: parentId, drive_id: driveId },
    }),

  getGoogleDriveSharedDrives: () =>
    api.get<{ shared_drives: GoogleDriveSharedDrive[] }>(
      '/oauth_connections/google_drive/shared_drives',
    ),

  getOneDriveFolders: (parentId?: string, driveId?: string) =>
    api.get<{
      folders: OneDriveFolder[];
      parent_id?: string;
      drive_id?: string;
    }>('/oauth_connections/onedrive/folders', {
      params: { parent_id: parentId, drive_id: driveId },
    }),

  getOneDriveDrives: () =>
    api.get<{ drives: OneDriveDrive[] }>('/oauth_connections/onedrive/drives'),

  getJiraSites: () =>
    api.get<{ sites: JiraSite[] }>('/oauth_connections/jira/sites'),

  getJiraProjects: (cloudId: string) =>
    api.get<{ projects: JiraProject[]; cloud_id: string }>(
      '/oauth_connections/jira/projects',
      { params: { cloud_id: cloudId } },
    ),
};

// Project Integrations API
export const projectIntegrationsAPI = {
  get: (projectId: number) =>
    api.get<ProjectIntegrations>(`/projects/${projectId}/integrations`),

  connectGoogleDrive: (
    projectId: number,
    data: {
      folder_id: string;
      folder_name?: string;
      folder_path?: string;
      drive_id?: string | null;
      drive_name?: string;
      is_shared_drive?: boolean;
    },
  ) => api.post(`/projects/${projectId}/integrations/google_drive`, data),

  updateGoogleDrive: (
    projectId: number,
    data: {
      folder_id?: string;
      folder_name?: string;
      folder_path?: string;
      sync_enabled?: boolean;
    },
  ) => api.put(`/projects/${projectId}/integrations/google_drive`, data),

  connectGitHub: (
    projectId: number,
    data: {
      repo_full_name: string;
      repo_name?: string;
      default_branch?: string;
      sync_commits?: boolean;
      sync_prs?: boolean;
      sync_issues?: boolean;
      sync_code?: boolean;
    },
  ) => api.post(`/projects/${projectId}/integrations/github`, data),

  updateGitHub: (
    projectId: number,
    integrationId: number,
    data: {
      default_branch?: string;
      sync_commits?: boolean;
      sync_prs?: boolean;
      sync_issues?: boolean;
      sync_code?: boolean;
      sync_enabled?: boolean;
    },
  ) =>
    api.put(
      `/projects/${projectId}/integrations/github/${integrationId}`,
      data,
    ),

  connectOneDrive: (
    projectId: number,
    data: {
      folder_id: string;
      folder_name?: string;
      folder_path?: string;
      drive_id?: string;
    },
  ) => api.post(`/projects/${projectId}/integrations/onedrive`, data),

  updateOneDrive: (
    projectId: number,
    data: {
      folder_id?: string;
      folder_name?: string;
      folder_path?: string;
      sync_enabled?: boolean;
    },
  ) => api.put(`/projects/${projectId}/integrations/onedrive`, data),

  connectJira: (
    projectId: number,
    data: {
      cloud_id: string;
      site_name?: string;
      site_url?: string;
      jira_project_id?: string;
      jira_project_key?: string;
      jira_project_name?: string;
      sync_issues?: boolean;
      sync_sprints?: boolean;
      sync_epics?: boolean;
    },
  ) => api.post(`/projects/${projectId}/integrations/jira`, data),

  updateJira: (
    projectId: number,
    integrationId: number,
    data: {
      sync_issues?: boolean;
      sync_sprints?: boolean;
      sync_epics?: boolean;
      sync_enabled?: boolean;
    },
  ) =>
    api.put(`/projects/${projectId}/integrations/jira/${integrationId}`, data),

  disconnect: (
    projectId: number,
    type: 'google_drive' | 'onedrive' | 'github' | 'jira',
    integrationId?: number,
  ) =>
    api.delete(
      `/projects/${projectId}/integrations/${type}${integrationId ? `?id=${integrationId}` : ''}`,
    ),

  sync: (
    projectId: number,
    type: 'google_drive' | 'onedrive' | 'github' | 'jira',
    integrationId?: number,
  ) =>
    api.post(
      `/projects/${projectId}/integrations/${type}/sync${integrationId ? `?id=${integrationId}` : ''}`,
    ),

  getLogs: (
    projectId: number,
    type: 'google_drive' | 'onedrive' | 'github' | 'jira',
  ) =>
    api.get<{ logs: IntegrationSyncLog[] }>(
      `/projects/${projectId}/integrations/${type}/logs`,
    ),
};

// Billing Types
export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  price_cents: number;
  price: number;
  formatted_price: string;
  currency: string;
  billing_interval: 'month' | 'year';
  user_limit: number | null;
  ai_analysis_limit: number | null;
  time_entry_limit: number | null;
  features: string[];
  stripe_price_id: string;
}

export interface Subscription {
  id: number;
  status: string;
  quantity: number;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at?: string;
  trial_start_date?: string;
  trial_end_date?: string;
  trial_days_remaining: number;
  monthly_price: number;
  plan: {
    id: number;
    name: string;
    price_cents: number;
    billing_interval: string;
    user_limit: number | null;
    ai_analysis_limit: number | null;
    time_entry_limit: number | null;
  } | null;
}

export interface Invoice {
  id: number;
  stripe_invoice_id: string;
  status: string;
  invoice_number?: string;
  subtotal: number;
  tax: number;
  total: number;
  formatted_total: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  due_date?: string;
  paid_at?: string;
  hosted_invoice_url?: string;
  invoice_pdf_url?: string;
  line_items: Array<{
    description: string;
    amount: number;
    currency: string;
    quantity?: number;
    period_start?: string;
    period_end?: string;
    proration?: boolean;
  }>;
  created_at: string;
}

export interface PaymentMethod {
  id: number;
  payment_type: string;
  card_brand?: string;
  card_last_four?: string;
  expiry_display?: string;
  is_default: boolean;
  display_name: string;
  expired: boolean;
  expiring_soon: boolean;
  created_at: string;
}

export interface UsageSummary {
  users: { current: number; limit: number | null; percentage: number | null };
  time_entries: {
    current: number;
    limit: number | null;
    percentage: number | null;
  };
  ai_analysis: {
    current: number;
    limit: number | null;
    percentage: number | null;
  };
}

export interface UsageWarning {
  metric: string;
  status: 'warning' | 'critical' | 'exceeded';
  percentage: number;
}

// Billing API
export const billingAPI = {
  // Plans
  getPlans: () => api.get<{ plans: SubscriptionPlan[] }>('/billing/plans'),
  getPlan: (id: number) =>
    api.get<{ plan: SubscriptionPlan }>(`/billing/plans/${id}`),

  // Subscriptions
  getCurrentSubscription: () =>
    api.get<{
      subscription: Subscription | null;
      has_subscription: boolean;
      trialing: boolean;
      trial_days_remaining: number;
    }>('/billing/subscriptions/current'),

  createSubscription: (planId: number, quantity?: number) =>
    api.post<{ subscription: Subscription; client_secret?: string }>(
      '/billing/subscriptions',
      {
        plan_id: planId,
        quantity: quantity || 1,
      },
    ),

  updateSubscription: (
    id: number,
    data: { plan_id?: number; quantity?: number },
  ) =>
    api.patch<{ subscription: Subscription }>(
      `/billing/subscriptions/${id}`,
      data,
    ),

  cancelSubscription: (id: number, atPeriodEnd?: boolean) =>
    api.post<{ subscription: Subscription }>(
      `/billing/subscriptions/${id}/cancel`,
      {
        at_period_end: atPeriodEnd ?? true,
      },
    ),

  reactivateSubscription: (id: number) =>
    api.post<{ subscription: Subscription }>(
      `/billing/subscriptions/${id}/reactivate`,
    ),

  previewChange: (id: number, data: { plan_id?: number; quantity?: number }) =>
    api.get<{
      preview: {
        proration_amount: number;
        new_total: number;
        currency: string;
        line_items: Array<{ description: string; amount: number }>;
      };
    }>(`/billing/subscriptions/${id}/preview_change`, { params: data }),

  createCheckoutSession: (
    planId: number,
    quantity: number,
    successUrl: string,
    cancelUrl: string,
  ) =>
    api.post<{ session_id: string; url: string }>(
      '/billing/subscriptions/checkout_session',
      {
        plan_id: planId,
        quantity,
        success_url: successUrl,
        cancel_url: cancelUrl,
      },
    ),

  getBillingPortalUrl: (returnUrl: string) =>
    api.get<{ url: string }>('/billing/subscriptions/billing_portal', {
      params: { return_url: returnUrl },
    }),

  // Invoices
  getInvoices: (limit?: number) =>
    api.get<{ invoices: Invoice[] }>('/billing/invoices', {
      params: { limit },
    }),

  getInvoice: (id: number) =>
    api.get<{ invoice: Invoice }>(`/billing/invoices/${id}`),

  getUpcomingInvoice: () =>
    api.get<{
      upcoming_invoice: {
        subtotal: number;
        tax: number;
        total: number;
        currency: string;
        period_start: string;
        period_end: string;
        line_items: Array<{ description: string; amount: number }>;
      } | null;
    }>('/billing/invoices/upcoming'),

  // Payment Methods
  getPaymentMethods: () =>
    api.get<{ payment_methods: PaymentMethod[] }>('/billing/payment_methods'),

  createSetupIntent: () =>
    api.post<{ client_secret: string }>(
      '/billing/payment_methods/setup_intent',
    ),

  addPaymentMethod: (stripePaymentMethodId: string, setDefault?: boolean) =>
    api.post<{ payment_method: PaymentMethod }>('/billing/payment_methods', {
      stripe_payment_method_id: stripePaymentMethodId,
      set_default: setDefault,
    }),

  setDefaultPaymentMethod: (id: number) =>
    api.post<{ payment_method: PaymentMethod }>(
      `/billing/payment_methods/${id}/set_default`,
    ),

  removePaymentMethod: (id: number) =>
    api.delete(`/billing/payment_methods/${id}`),

  // Usage
  getCurrentUsage: () =>
    api.get<{ usage: UsageSummary; warnings: UsageWarning[]; period: string }>(
      '/billing/usage/current',
    ),

  getUsageHistory: (months?: number) =>
    api.get<{
      history: Array<{
        period: string;
        users: number;
        time_entries: number;
        ai_analysis: number;
      }>;
    }>('/billing/usage/history', { params: { months } }),

  checkUsageLimit: (actionType: string) =>
    api.get<{
      allowed: boolean;
      current: number;
      limit: number | null;
      percentage: number | null;
    }>('/billing/usage/check', { params: { action_type: actionType } }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Kanban Board API
export const boardsAPI = {
  // Get or create board for a project
  getBoard: (projectId: number, boardType?: 'kanban' | 'scrum') =>
    api.get(`/projects/${projectId}/board`, {
      params: { board_type: boardType },
    }),

  // Update board settings
  updateBoard: (
    projectId: number,
    data: {
      name?: string;
      board_type?: string;
      default_sprint_duration_weeks?: number;
      active?: boolean;
    },
  ) => api.put(`/projects/${projectId}/board`, { board: data }),

  // Column management
  createColumn: (
    projectId: number,
    data: {
      name: string;
      wip_limit?: number;
      column_type?: string;
      maps_to_status?: string;
      color?: string;
    },
  ) => api.post(`/projects/${projectId}/board/columns`, { column: data }),

  updateColumn: (
    projectId: number,
    columnId: number,
    data: {
      name?: string;
      wip_limit?: number;
      column_type?: string;
      maps_to_status?: string;
      color?: string;
    },
  ) =>
    api.put(`/projects/${projectId}/board/columns/${columnId}`, {
      column: data,
    }),

  deleteColumn: (projectId: number, columnId: number) =>
    api.delete(`/projects/${projectId}/board/columns/${columnId}`),

  reorderColumns: (projectId: number, columnIds: number[]) =>
    api.post(`/projects/${projectId}/board/columns/reorder`, {
      column_ids: columnIds,
    }),
};

// Sprint API
export const sprintsAPI = {
  // List sprints for a project
  list: (projectId: number) => api.get(`/projects/${projectId}/sprints`),

  // Get active sprint
  getActive: (projectId: number) =>
    api.get(`/projects/${projectId}/sprints/active`),

  // Get single sprint
  get: (projectId: number, sprintId: number) =>
    api.get(`/projects/${projectId}/sprints/${sprintId}`),

  // Create sprint
  create: (
    projectId: number,
    data: {
      name: string;
      goal?: string;
      start_date?: string;
      end_date?: string;
      planned_velocity?: number;
    },
  ) => api.post(`/projects/${projectId}/sprints`, { sprint: data }),

  // Update sprint
  update: (
    projectId: number,
    sprintId: number,
    data: {
      name?: string;
      goal?: string;
      start_date?: string;
      end_date?: string;
      planned_velocity?: number;
    },
  ) => api.put(`/projects/${projectId}/sprints/${sprintId}`, { sprint: data }),

  // Delete sprint
  delete: (projectId: number, sprintId: number) =>
    api.delete(`/projects/${projectId}/sprints/${sprintId}`),

  // Start sprint
  start: (projectId: number, sprintId: number) =>
    api.post(`/projects/${projectId}/sprints/${sprintId}/start`),

  // Complete sprint
  complete: (
    projectId: number,
    sprintId: number,
    retrospectiveNotes?: string,
  ) =>
    api.post(`/projects/${projectId}/sprints/${sprintId}/complete`, {
      retrospective_notes: retrospectiveNotes,
    }),

  // Add tasks to sprint
  addTasks: (projectId: number, sprintId: number, taskIds: number[]) =>
    api.post(`/projects/${projectId}/sprints/${sprintId}/add_tasks`, {
      task_ids: taskIds,
    }),

  // Remove tasks from sprint
  removeTasks: (projectId: number, sprintId: number, taskIds: number[]) =>
    api.delete(`/projects/${projectId}/sprints/${sprintId}/remove_tasks`, {
      data: { task_ids: taskIds },
    }),
};

// Epic API
export const epicsAPI = {
  // List epics for a project
  list: (projectId: number) => api.get(`/projects/${projectId}/epics`),

  // Get single epic
  get: (projectId: number, epicId: number) =>
    api.get(`/projects/${projectId}/epics/${epicId}`),

  // Create epic
  create: (
    projectId: number,
    data: {
      title: string;
      description?: string;
      color?: string;
      status?: string;
      priority?: number;
      start_date?: string;
      target_date?: string;
      owner_user_id?: number;
    },
  ) => api.post(`/projects/${projectId}/epics`, { epic: data }),

  // Update epic
  update: (
    projectId: number,
    epicId: number,
    data: {
      title?: string;
      description?: string;
      color?: string;
      status?: string;
      priority?: number;
      start_date?: string;
      target_date?: string;
      owner_user_id?: number;
    },
  ) => api.put(`/projects/${projectId}/epics/${epicId}`, { epic: data }),

  // Delete epic
  delete: (projectId: number, epicId: number) =>
    api.delete(`/projects/${projectId}/epics/${epicId}`),

  // Add tasks to epic
  addTasks: (projectId: number, epicId: number, taskIds: number[]) =>
    api.post(`/projects/${projectId}/epics/${epicId}/add_tasks`, {
      task_ids: taskIds,
    }),

  // Remove tasks from epic
  removeTasks: (projectId: number, epicId: number, taskIds: number[]) =>
    api.delete(`/projects/${projectId}/epics/${epicId}/remove_tasks`, {
      data: { task_ids: taskIds },
    }),
};

// Organization API
export interface Organization {
  id: number;
  name: string;
  address?: string;
  contact_email?: string;
  billing_email?: string;
  fiscal_year_start_month: number;
  timer_tag_selection?: 'single' | 'multiple';
  has_subscription?: boolean;
  trialing?: boolean;
  trial_days_remaining?: number;
  subscription_status?: string;
}

export const organizationsAPI = {
  get: (id: number) =>
    api.get<{ organization: Organization }>(`/organizations/${id}`),

  update: (
    id: number,
    data: {
      fiscal_year_start_month?: number;
      timer_tag_selection?: 'single' | 'multiple';
    },
  ) =>
    api.put<{ organization: Organization }>(`/organizations/${id}`, {
      organization: data,
    }),
};

// Task Kanban API extensions
export const taskKanbanAPI = {
  // Move task to column
  move: (taskId: number, boardColumnId: number, position?: number) =>
    api.patch(`/tasks/${taskId}/move`, {
      board_column_id: boardColumnId,
      position,
    }),

  // Assign task to sprint
  assignSprint: (taskId: number, sprintId: number | null) =>
    api.patch(`/tasks/${taskId}/assign_sprint`, { sprint_id: sprintId }),

  // Assign task to epic
  assignEpic: (taskId: number, epicId: number | null) =>
    api.patch(`/tasks/${taskId}/assign_epic`, { epic_id: epicId }),

  // Bulk move tasks
  bulkMove: (taskIds: number[], boardColumnId?: number, sprintId?: number) =>
    api.patch('/tasks/bulk_move', {
      task_ids: taskIds,
      board_column_id: boardColumnId,
      sprint_id: sprintId,
    }),

  // Get backlog tasks
  getBacklog: (projectId: number) => api.get(`/projects/${projectId}/backlog`),
};

// Reminders API
export interface Reminder {
  id: string;
  type:
    | 'idle_timer'
    | 'approval_pending'
    | 'deadline_overdue'
    | 'daily_summary';
  title: string;
  message: string;
  dismiss_id: string;
  created_at: string;
  data: Record<string, unknown>;
}

export const remindersAPI = {
  getActive: () => api.get<{ reminders: Reminder[] }>('/reminders/active'),

  dismiss: (dismissId: string) => api.post(`/reminders/${dismissId}/dismiss`),
};

// Expense Limits API
export const expenseLimitsAPI = {
  getAll: () => api.get('/expense_limits'),
  upsert: (limits: Partial<ExpenseLimit>[]) =>
    api.patch('/expense_limits', { expense_limits: { limits } }),
};

export default api;
